import React, { useEffect, useState } from 'react'
import { Box, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from '@material-ui/core'
import { useServices } from 'cng-web-lib'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import moment from 'moment-timezone'
import UaeMpciApiUrls from 'src/apiUrls/UaeMpciApiUrls'
import RemarkCell from './RemarkCell'
import { getStatusLabel, getSubStatusLabel, STATUS_COLOR } from '../constants/auditStatuses'

function normalizePartyIndex(text) {
  if (!text) return text
  return text.replace(/\[(\d+)\]/g, (_, n) => `[${parseInt(n, 10) + 1}]`)
}

function AuditHistoryTable({ id }) {
  const { securedSendRequest } = useServices()
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const theme = useTheme()

  useEffect(() => {
    if (!id) return
    setLoading(true)
    securedSendRequest.execute(
      'POST',
      UaeMpciApiUrls.AUDIT_LOG_GET,
      { manifestHeaderId: id },
      (res) => {
        const data = res?.data
        const list = data?.data ?? data
        setLogs(Array.isArray(list) ? list : [])
        setLoading(false)
      },
      () => setLoading(false),
      () => {},
      {},
      null
    )
  }, [id])

  if (loading) {
    return (
      <Box display='flex' alignItems='center' style={{ gap: 8, padding: '12px 0' }}>
        <FontAwesomeIcon icon={['fal', 'spinner']} spin />
        <Typography variant='body2' color='textSecondary'>Loading history...</Typography>
      </Box>
    )
  }

  if (logs.length === 0) {
    return (
      <Box padding={2} textAlign='center'>
        <Typography variant='body2' color='textSecondary'>No audit history found.</Typography>
      </Box>
    )
  }

  return (
    <TableContainer component={Paper} variant='outlined'>
      <Table size='small'>
        <TableHead>
          <TableRow style={{ backgroundColor: theme.palette.background.sectionOddBg }}>
            <TableCell><Typography variant='caption' style={{ fontWeight: 700 }}>Master Bill of Lading Number</Typography></TableCell>
            <TableCell style={{ whiteSpace: 'nowrap' }}><Typography variant='caption' style={{ fontWeight: 700 }}>House Bill of Lading Number</Typography></TableCell>
            <TableCell><Typography variant='caption' style={{ fontWeight: 700 }}>MPCI Document ID/Transaction Id</Typography></TableCell>
            <TableCell><Typography variant='caption' style={{ fontWeight: 700 }}>Status</Typography></TableCell>
            <TableCell><Typography variant='caption' style={{ fontWeight: 700 }}>Date / Time</Typography></TableCell>
            <TableCell><Typography variant='caption' style={{ fontWeight: 700 }}>Remarks</Typography></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {logs.map((log, idx) => {
            const statusColor = STATUS_COLOR[log.status] || { bg: '#eeeeee', text: '#424242' }
            const subStatusLabel = log.subStatus ? getSubStatusLabel(log.subStatus) : null
            const chipLabel = subStatusLabel
              ? `${getStatusLabel(log.status)} (${subStatusLabel})`
              : getStatusLabel(log.status)

            return (
              <TableRow key={log.id ?? idx} hover>
                <TableCell>
                  <RemarkCell value={log.masterBillNo || '—'} />
                </TableCell>
                <TableCell>
                  <RemarkCell value={log.houseBillNo || '—'} />
                </TableCell>
                <TableCell>
                  <Typography variant='caption' style={{ fontFamily: 'monospace' }}>
                    {log.mpciDocId || log.docId || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {log.status ? (
                    <Chip
                      label={chipLabel}
                      size='small'
                      style={{
                        fontWeight: 600,
                        fontSize: 11,
                        backgroundColor: statusColor.bg,
                        color: statusColor.text
                      }}
                    />
                  ) : '—'}
                </TableCell>
                <TableCell>
                  <Typography variant='caption'>
                    {log.createdDate
                      ? moment(log.createdDate).format('D MMM YYYY, HH:mm z')
                      : log.dateTime
                      ? moment(log.dateTime).format('D MMM YYYY, HH:mm z')
                      : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <RemarkCell value={normalizePartyIndex(log.remarks || log.description || '')} />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default AuditHistoryTable
